import React, { useState } from 'react';
import { Modal, Button } from 'components/common';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { components } from 'react-select';
import { createTask as _createTask, updateTask as _updateTask } from 'redux/crm/project/projectActivitySlice';
import TinymceEditor from 'components/common/TinymceEditor';
import ContactSelect  from './ContactSelect';
import AssigneeSelect from './AssigneeSelect';
import FileUploader from './FileUploader';
import { INPUT_CLS } from 'helpers/formStyles';

// ── Priority config ────────────────────────────────────────────────────────────
const PRIORITY_OPTIONS = [
  { value: 'low',    label: 'Low',    dot: '#3b82f6', icon: '⬇' },
  { value: 'medium', label: 'Medium', dot: '#f59e0b', icon: '➡' },
  { value: 'high',   label: 'High',   dot: '#ef4444', icon: '⬆' },
];

// ── Status config ──────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'todo',        label: 'To do',       dot: '#94a3b8', bg: '#f1f5f9' },
  { value: 'in_progress', label: 'In progress', dot: '#3b82f6', bg: '#eff6ff' },
  { value: 'done',        label: 'Done',        dot: '#22c55e', bg: '#f0fdf4' },
];

const dotStyle = (color) => ({
  display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
  backgroundColor: color, marginRight: 8, flexShrink: 0,
});

const PriorityOption = (props) => {
  const { data, isSelected, isFocused } = props;
  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', borderRadius: 6,
        backgroundColor: isSelected ? '#4f46e5' : isFocused ? '#f8fafc' : 'transparent',
        color: isSelected ? '#fff' : '#1e293b', cursor: 'pointer' }}>
        <span style={dotStyle(isSelected ? '#fff' : data.dot)} />
        <span style={{ fontWeight: 600, fontSize: 13 }}>{data.label}</span>
      </div>
    </components.Option>
  );
};

const StatusOption = (props) => {
  const { data, isSelected, isFocused } = props;
  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', borderRadius: 6,
        backgroundColor: isSelected ? '#4f46e5' : isFocused ? '#f8fafc' : 'transparent',
        color: isSelected ? '#fff' : '#1e293b', cursor: 'pointer' }}>
        <span style={dotStyle(isSelected ? '#fff' : data.dot)} />
        <span style={{ fontSize: 13 }}>{data.label}</span>
      </div>
    </components.Option>
  );
};

const StatusSingleValue = (props) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ ...dotStyle(data.dot), flexShrink: 0 }} />
        <span style={{ fontWeight: 600, fontSize: 13 }}>{data.label}</span>
      </div>
    </components.SingleValue>
  );
};

const PrioritySingleValue = (props) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ ...dotStyle(data.dot), flexShrink: 0 }} />
        <span style={{ fontWeight: 700, fontSize: 13, color: data.dot }}>{data.label}</span>
      </div>
    </components.SingleValue>
  );
};

const optionDataShape = PropTypes.shape({
  dot:   PropTypes.string,
  label: PropTypes.string,
});

PriorityOption.propTypes = {
  data:       optionDataShape.isRequired,
  isSelected: PropTypes.bool,
  isFocused:  PropTypes.bool,
};

StatusOption.propTypes = {
  data:       optionDataShape.isRequired,
  isSelected: PropTypes.bool,
  isFocused:  PropTypes.bool,
};

StatusSingleValue.propTypes = {
  data: optionDataShape.isRequired,
};

PrioritySingleValue.propTypes = {
  data: optionDataShape.isRequired,
};

const selectStyles = {
  control: (base, state) => ({
    ...base, minHeight: 38, borderRadius: 8,
    borderColor: state.isFocused ? '#4f46e5' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(79,70,229,0.12)' : 'none',
    '&:hover': { borderColor: '#4f46e5' }, transition: 'all 0.15s ease',
  }),
  menu: (base) => ({ ...base, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0', zIndex: 100001 }),
  menuPortal: (base) => ({ ...base, zIndex: 100001 }),
  menuList: (base) => ({ ...base, padding: 4 }),
  option: (base) => ({ ...base, padding: 0, backgroundColor: 'transparent', '&:active': { backgroundColor: 'transparent' } }),
  valueContainer: (base) => ({ ...base, padding: '0 10px' }),
  indicatorSeparator: () => ({ display: 'none' }),
};

const TASK_TYPES = ['Sales Call', 'Demo', 'Follow Up', 'Research', 'Meeting', 'Documentation', 'Other'];

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = () => ({ name: '', due_date: today(), due_time: '', priority: 'medium', status: 'todo', task_type: '', description: '' });

const TaskForm = ({
  open, onClose, projectId, record,
  createAction = _createTask,
  updateAction = _updateTask,
  submittingSelector = s => s.crm_project_activity.submitting,
}) => {
  const dispatch   = useDispatch();
  const submitting = useSelector(submittingSelector);

  const [form, setForm] = useState(() => record ? {
    name:        record.name        || '',
    due_date:    record.due_date    || '',
    due_time:    record.due_time    || '',
    priority:    record.priority    || 'medium',
    status:      record.status      || 'todo',
    task_type:   record.task_type   || '',
    description: record.description || '',
  } : EMPTY());

  const [contact,  setContact]  = useState(
    record?.contact_id ? { id: record.contact_id, name: record.contact_name || '' } : null
  );
  const [assignee, setAssignee] = useState(
    record?.assignee_id ? { id: record.assignee_id, name: record.assignee_name || '' } : null
  );
  const [files, setFiles] = useState([]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Task name is required.'); return; }

    const data = {
      ...form,
      contact_id:  contact?.id  || null,
      assignee_id: assignee?.id || null,
    };

    const action = record
      ? updateAction({ projectId, id: record.id, data, files })
      : createAction({ projectId, data, files });

    const res = await dispatch(action);
    if (res.meta.requestStatus === 'fulfilled') {
      toast.success(record ? 'Task updated.' : 'Task created.');
      onClose();
    } else {
      toast.error(res.payload || 'Failed to save task.');
    }
  };

  const inputClass = INPUT_CLS;
  const selClass   = `${inputClass} tw-bg-white`;
  const labelClass = "tw-text-xs tw-font-semibold tw-text-slate-600 tw-mb-1 tw-block";

  return (
    <Modal show={open} onHide={onClose} title={record ? 'Edit Task' : 'Add Task'} size="xl" backdropClick={false}>
      <form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-4">
        {/* Task name */}
        <div>
          <label className={labelClass}>Task name <span className="tw-text-red-500">*</span></label>
          <input value={form.name} onChange={e => set('name', e.target.value)}
            placeholder="Task name" className={inputClass} />
        </div>

        {/* Contact + Assignee */}
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          <ContactSelect
            label="Contact"
            value={contact}
            onChange={setContact}
            projectId={projectId}
          />
          <AssigneeSelect
            label="Assignee"
            value={assignee}
            onChange={setAssignee}
          />
        </div>

        {/* Due date + time */}
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          <div>
            <label className={labelClass}>Due date</label>
            <input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Due time</label>
            <input type="time" value={form.due_time} onChange={e => set('due_time', e.target.value)} className={inputClass} />
          </div>
        </div>

        {/* Priority + Status + Task type */}
        <div className="tw-grid tw-grid-cols-3 tw-gap-3">
          <div>
            <label className={labelClass}>Priority <span className="tw-text-red-500">*</span></label>
            <Select
              options={PRIORITY_OPTIONS}
              value={PRIORITY_OPTIONS.find(p => p.value === form.priority) || null}
              onChange={opt => set('priority', opt?.value)}
              styles={selectStyles}
              menuPortalTarget={document.body}
              isSearchable={false}
              components={{ Option: PriorityOption, SingleValue: PrioritySingleValue, IndicatorSeparator: () => null }}
            />
          </div>
          <div>
            <label className={labelClass}>Status <span className="tw-text-red-500">*</span></label>
            <Select
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find(s => s.value === form.status) || null}
              onChange={opt => set('status', opt?.value)}
              styles={selectStyles}
              menuPortalTarget={document.body}
              isSearchable={false}
              components={{ Option: StatusOption, SingleValue: StatusSingleValue, IndicatorSeparator: () => null }}
            />
          </div>
          <div>
            <label className={labelClass}>Task type</label>
            <select value={form.task_type} onChange={e => set('task_type', e.target.value)} className={selClass}>
              <option value="">Task type</option>
              {TASK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className={labelClass}>Description</label>
          <TinymceEditor
            value={form.description}
            handleChange={(val) => set('description', val)}
            height="220px"
            initOverride={{
              menubar:           false,
              toolbar:           'bold italic underline | bullist numlist | link | image',
              plugins:           'lists link image',
              paste_data_images: true,
            }}
          />
        </div>

        {/* File upload */}
        <div>
          <label className={labelClass}>Attachments</label>
          <FileUploader
            files={files}
            onChange={setFiles}
            existingAttachments={record?.attachments || []}
          />
        </div>

        {/* Actions */}
        <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-1">
          <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

TaskForm.propTypes = {
  open:      PropTypes.bool.isRequired,
  onClose:   PropTypes.func.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  record:    PropTypes.object,
  createAction:       PropTypes.func,
  updateAction:       PropTypes.func,
  submittingSelector: PropTypes.func,
};

export default TaskForm;
